import { useNavigate } from "react-router-dom";
import { Calendar, Repeat, Plane, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Picker modal that asks the user whether they want to create a one-off game,
 * a recurring series, or a trip/retreat.
 */
const CreateEventPicker = ({ open, onOpenChange, prefill, fromHost }) => {
  const navigate = useNavigate();

  const params = { ...(prefill || {}) };
  if (fromHost) params.from = "host";
  const qs = Object.keys(params).length
    ? "?" + new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString()
    : "";
  const pickOneOff = () => { onOpenChange(false); navigate("/create/game" + qs); };
  const pickSeries = () => { onOpenChange(false); navigate("/create/series" + qs); };
  const pickTrip   = () => { onOpenChange(false); navigate("/create/trip" + qs); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-3xl" data-testid="create-event-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Create an offering</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Pick the type of offering you want to host. You can always switch later.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          <button
            type="button"
            onClick={pickOneOff}
            className="group text-left p-5 rounded-2xl border border-border hover:border-primary hover:bg-primary/5 transition-all"
            data-testid="picker-one-off"
          >
            <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
              <Calendar className="w-5 h-5" />
            </div>
            <h3 className="font-display text-lg leading-tight mb-1">Single session</h3>
            <p className="text-sm text-muted-foreground">A one-off class, session or appointment on a specific date.</p>
          </button>

          <button
            type="button"
            onClick={pickSeries}
            className="group text-left p-5 rounded-2xl border border-border hover:border-primary hover:bg-primary/5 transition-all"
            data-testid="picker-series"
          >
            <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
              <Repeat className="w-5 h-5" />
            </div>
            <h3 className="font-display text-lg leading-tight mb-1">Series or league</h3>
            <p className="text-sm text-muted-foreground">
              A connected set of sessions — a course, league or membership — booked as one.
              <span className="block mt-1 text-xs italic">(Individual sessions can still be booked one-by-one.)</span>
            </p>
          </button>

          <button
            type="button"
            onClick={pickTrip}
            className="group text-left p-5 rounded-2xl border border-border hover:border-primary hover:bg-primary/5 transition-all"
            data-testid="picker-trip"
          >
            <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
              <Plane className="w-5 h-5" />
            </div>
            <h3 className="font-display text-lg leading-tight mb-1">Trip or retreat</h3>
            <p className="text-sm text-muted-foreground">
              A multi-day experience guests book in full.
            </p>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEventPicker;
